<template>
  <HeadlessListbox #default="{ open }" as="div">
    <HeadlessListboxLabel
      v-if="label"
      class="mb-1 block text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </HeadlessListboxLabel>
    <HeadlessListboxButton
      :class="[
        'flex w-full flex-nowrap items-center justify-between rounded-md border border-background-fg/8 px-3 py-2 text-sm font-semibold focus:outline-none sm:text-sm',
        hasError
          ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
          : 'focus:border-primary focus:ring-primary',
      ]"
    >
      <span class="mr-2 line-clamp-1">
        {{ selectedLabel }}
      </span>
      <IconChevronDown
        class="rounded-full text-background-fg/40"
      />
    </HeadlessListboxButton>
    <template v-if="hasError">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>

    <BaseDrawer shrink :is-open="open">
      <HeadlessListboxOptions
        static
        class="mb-6 h-full overflow-y-auto"
      >
        <HeadlessListboxOption
          v-for="option in options"
          :key="option.id ?? undefined"
          as="template"
        >
          <li>
            <div
              :class="[
                inputValue === option.id
                  ? 'bg-background-fg/5'
                  : 'text-background-fg/50 hover:bg-background-fg/3',
                'line-clamp-1 block cursor-pointer px-3 py-2 text-left text-sm font-semibold',
              ]"
              @click="setModelValue(option.id)"
            >
              {{ option.label }}
            </div>
          </li>
        </HeadlessListboxOption>
      </HeadlessListboxOptions>
    </BaseDrawer>
  </HeadlessListbox>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"

type Option = {
  id: string | null
  label: string
}

const props = defineProps<{
  modelValue?: string | null
  name: string
  options: Option[]
  label?: string
}>()

const emit = defineEmits(["update:modelValue"])
onMounted(() => {
  emit("update:modelValue", props.modelValue)
})

const { value: inputValue, errorMessage } = useField<
  string | null
>(props.name, undefined, {
  initialValue: props.modelValue,
})

const hasError = computed(() => !!errorMessage.value)

const setModelValue = (id: string | null) => {
  inputValue.value = id
}

const selectedLabel = computed(
  () =>
    props.options.find(({ id }) => id === inputValue.value)
      ?.label || inputValue.value
)
</script>
